/**
 * Collection Tabs Carousel
 * CSS-only carousel with drag/scroll functionality
 * Optimized for performance with deferred loading
 * Supports multiple instances on the same page
 */

class CollectionTabsCarousel {
  constructor(sectionElement) {
    // Scope all queries to this specific section
    this.section = sectionElement;
    if (!this.section) return;

    this.carousels = this.section.querySelectorAll('.css-collection-carousel');
    this.tabButtons = this.section.querySelectorAll('[role="tab"]');
    this.tabPanels = this.section.querySelectorAll('[role="tabpanel"]');
    this.showThemeBadges = this.section.dataset.themeBadges === 'true';

    this.init();
  }

  init() {
    if (this.carousels.length === 0) return;

    this.initCarousels();
    this.initDeferredImageLoading();
    this.initLazyProductLoading();
    this.initWishlistStateSync();
  }

  /**
   * Initialize wishlist state synchronization for lazy-loaded buttons
   * This ensures buttons update their visual state after add/remove actions
   * by properly awaiting Appmate's async operations
   */
  initWishlistStateSync() {
    // Store reference to this instance for use in event handlers
    const self = this;

    // Wait for Appmate to be ready, then hook into its events
    const setupAppmateSync = () => {
      if (typeof Appmate === 'undefined' || !Appmate.wk) {
        // Retry after a short delay if Appmate isn't ready yet
        setTimeout(setupAppmateSync, 500);
        return;
      }

      // Use capturing phase to intercept clicks BEFORE Appmate's delegated handlers
      this.section.addEventListener('click', (e) => {
        const addButton = e.target.closest('[data-wk-add-product]');
        const removeButton = e.target.closest('[data-wk-remove-product]');

        // Only handle buttons within lazy-loaded/rendered containers
        const isLazyLoadedButton = (btn) => {
          return btn && btn.closest('.wk-button-rendered');
        };

        if (addButton && isLazyLoadedButton(addButton)) {
          const productId = addButton.getAttribute('data-wk-add-product');

          // Prevent Appmate's default handler from also processing this
          e.preventDefault();
          e.stopPropagation();

          // Call Appmate's addProduct directly and update state when promise resolves
          Appmate.wk.addProduct(productId).then(() => {
            self.updateWishlistButtonState(productId, true);
          }).catch((err) => {
            console.warn('[CollectionTabs] Failed to add product to wishlist:', err);
          });
        } else if (removeButton && isLazyLoadedButton(removeButton)) {
          const productId = removeButton.getAttribute('data-wk-remove-product');

          // Prevent Appmate's default handler from also processing this
          e.preventDefault();
          e.stopPropagation();

          // Call Appmate's removeProduct directly and update state when promise resolves
          Appmate.wk.removeProduct(productId).then(() => {
            self.updateWishlistButtonState(productId, false);
          }).catch((err) => {
            console.warn('[CollectionTabs] Failed to remove product from wishlist:', err);
          });
        }
      }, true); // Use capturing phase
    };

    // Start the setup process
    setupAppmateSync();
  }

  initCarousels() {
    this.carousels.forEach(carousel => {
      let isDragging = false;
      let startX;
      let startY;
      let scrollLeft;
      let velocity = 0;
      let lastX = 0;
      let lastTime = 0;
      let momentumId = null;

      carousel.addEventListener('mousedown', (e) => {
        // Don't interfere with buttons/inputs
        if (e.target.closest('button:not(.product-card__wishlist), input, select, textarea')) return;

        isDragging = false; // Start as false, only set true when actually moving
        startX = e.pageX;
        startY = e.pageY;
        scrollLeft = carousel.scrollLeft;
        velocity = 0;
        lastX = e.pageX;
        lastTime = Date.now();

        // Stop any existing momentum
        if (momentumId) {
          cancelAnimationFrame(momentumId);
          momentumId = null;
        }

        carousel.style.scrollBehavior = 'auto';
      });

      document.addEventListener('mousemove', (e) => {
        if (!startX) return; // No mousedown happened

        const moveX = e.pageX - startX;
        const moveY = e.pageY - startY;
        const distanceX = Math.abs(moveX);
        const distanceY = Math.abs(moveY);

        // Start dragging if we've moved past threshold and it's more horizontal than vertical
        if (!isDragging && distanceX > 5 && distanceX > distanceY) {
          isDragging = true;
          carousel.style.cursor = 'grabbing';
          carousel.classList.add('dragging');
        }

        if (!isDragging) return;

        e.preventDefault();

        // Calculate velocity for momentum
        const now = Date.now();
        const deltaTime = now - lastTime;
        if (deltaTime > 0) {
          velocity = (e.pageX - lastX) / deltaTime;
        }
        lastX = e.pageX;
        lastTime = now;

        // Scroll the carousel
        const walk = (startX - e.pageX) * 2;
        carousel.scrollLeft = scrollLeft + walk;
      });

      document.addEventListener('mouseup', () => {
        if (!startX) return; // No mousedown happened

        if (isDragging) {
          isDragging = false;
          carousel.style.cursor = 'grab';
          carousel.classList.remove('dragging');

          // Apply momentum if velocity is significant
          if (Math.abs(velocity) > 0.5) {
            const applyMomentum = () => {
              if (Math.abs(velocity) < 0.05) {
                momentumId = null;
                carousel.style.scrollBehavior = 'smooth';
                return;
              }

              carousel.scrollLeft -= velocity * 16;
              velocity *= 0.90; // Deceleration
              momentumId = requestAnimationFrame(applyMomentum);
            };
            applyMomentum();
          } else {
            carousel.style.scrollBehavior = 'smooth';
          }
        }

        // Reset tracking variables
        startX = null;
        startY = null;
      });

      // Prevent clicks only during momentum (not during regular dragging)
      carousel.addEventListener('click', (e) => {
        if (momentumId) {
          e.preventDefault();
          e.stopPropagation();
        }
      }, true);

      // Prevent text selection
      carousel.addEventListener('selectstart', (e) => {
        if (isDragging) e.preventDefault();
      });

      // Prevent default drag on images
      carousel.addEventListener('dragstart', (e) => {
        e.preventDefault();
      });
    });
  }

  initDeferredImageLoading() {
    if (!this.section || this.tabButtons.length === 0) return;

    // Preload next tab on hover (predictive loading)
    this.tabButtons.forEach((button, index) => {
      button.addEventListener('mouseenter', function() {
        const panel = this.tabPanels[index];
        if (panel) {
          // PERF: Load lazy tab content via AJAX
          if (panel.hasAttribute('data-lazy-load')) {
            this.loadLazyTabContent(panel);
          } else if (panel.getAttribute('data-images-loaded') === 'false') {
            this.loadTabImages(panel);
          }
        }
      }.bind(this), { once: true, passive: true });

      // Load on click (fallback for touch devices)
      button.addEventListener('click', function() {
        const panel = this.tabPanels[index];
        if (panel) {
          // PERF: Load lazy tab content via AJAX
          if (panel.hasAttribute('data-lazy-load')) {
            this.loadLazyTabContent(panel);
          } else if (panel.getAttribute('data-images-loaded') === 'false') {
            this.loadTabImages(panel);
          }
        }
      }.bind(this), { passive: true });
    });

    // Intersection Observer for progressive loading (when tab comes into view)
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            // PERF: Load lazy tab content via AJAX when visible
            if (entry.target.hasAttribute('data-lazy-load')) {
              this.loadLazyTabContent(entry.target);
            } else if (entry.target.getAttribute('data-images-loaded') === 'false') {
              this.loadTabImages(entry.target);
            }
          }
        });
      }, { rootMargin: '50px' });

      this.tabPanels.forEach(panel => {
        if (panel.hasAttribute('data-lazy-load') || panel.getAttribute('data-images-loaded') === 'false') {
          observer.observe(panel);
        }
      });
    }
  }

  /**
   * PERF: Lazy load tab content via Shopify's Section Rendering API
   * This significantly reduces initial DOM size and page weight
   */
  async loadLazyTabContent(panel) {
    if (!panel.hasAttribute('data-lazy-load')) return;
    if (panel.hasAttribute('data-loading')) return; // Prevent duplicate loads

    panel.setAttribute('data-loading', 'true');

    const collectionHandle = panel.getAttribute('data-collection-handle');
    const limit = panel.getAttribute('data-products-limit') || 8;

    if (!collectionHandle) {
      console.warn('No collection handle found for lazy tab');
      return;
    }

    try {
      // Fetch collection products from Shopify's AJAX API
      const response = await fetch(`/collections/${collectionHandle}/products.json?limit=${limit}`);
      if (!response.ok) throw new Error('Failed to fetch products');

      const data = await response.json();
      const products = data.products || [];

      if (products.length > 0) {
        const carousel = panel.querySelector('.css-collection-carousel');
        if (carousel) {
          // Build simple product cards (reduced DOM compared to full product-card snippet)
          const html = products.map(product => this.buildProductCard(product, collectionHandle)).join('');
          carousel.innerHTML = html;

          // Reinitialize carousel drag for new content
          this.initSingleCarousel(carousel);

          // Refresh Wishlist King (Appmate) to initialize wishlist buttons on new products
          this.refreshWishlistButtons();

          // Refresh Loox ratings for lazy-loaded products
          this.refreshLooxRatings();

          if (typeof CE2 !== 'undefined' && CE2.refresh) CE2.refresh();
        }
      }

      // Mark as loaded
      panel.removeAttribute('data-lazy-load');
      panel.removeAttribute('data-loading');
      panel.setAttribute('data-images-loaded', 'true');

    } catch (error) {
      console.error('Error loading lazy tab content:', error);
      panel.removeAttribute('data-loading');
    }
  }

  /**
   * Build a simplified product card HTML
   * PERF: Uses minimal DOM compared to full product-card.liquid
   */
  buildProductCard(product, collectionHandle) {
    const image = product.images && product.images[0] ? product.images[0].src : '';
    const imageUrl = image ? image.replace(/\.([^.]+)$/, '_400x.$1') : '';

    // Get price from first variant
    // The collection API can return prices as strings - usually in cents (e.g., "7000" for $70)
    // but some products may have prices stored differently
    let priceValue = 0;
    if (product.variants && product.variants.length > 0 && product.variants[0].price) {
      priceValue = parseFloat(product.variants[0].price) || 0;
    }
    // Detect format: if price > 1000, assume cents and divide by 100; otherwise treat as dollars
    // This handles both "7000" (cents) and "70.00" (dollars) formats
    const priceInDollars = priceValue > 1000 ? priceValue / 100 : priceValue;
    // Format price: remove trailing zeros like Shopify's money_without_trailing_zeros
    const formattedPrice = priceInDollars % 1 === 0 ? priceInDollars.toFixed(0) : priceInDollars.toFixed(2);

    const vendor = product.vendor || '';

    // Split title on dash like the original product-card
    const titleParts = product.title.split(' – ');
    const titleHtml = titleParts.length > 1
      ? `<span class="block">${titleParts[0]}</span><span class="block">${titleParts[1]}</span>`
      : product.title;

    // Wishlist button placeholder - Appmate will render the actual button with correct state
    // Using a wrapper div that Appmate's render() will populate with the proper wishlist-button-collection template
    const wishlistPlaceholder = `<div class="wk-lazy-button" data-wk-product-id="${product.id}"></div>`;

    return `
      <div class="collection-carousel__slide px-8">
        <product-card class="product-card relative block" data-product-id="${product.id}">
          <a href="/collections/${collectionHandle}/products/${product.handle}" class="product-card__image collection-button active relative block">
            ${this.showThemeBadges && !product.available ? '<div class="product-card__soldout-tag absolute z-[1] bg-gray-light opacity-80 rounded-[4px] text-12 top-8 right-8 py-4 px-8">SOLD OUT</div>' : ''}
            <div class="relative">
              <div class="product-card__image-featured">
                <picture class="lazypicture block w-full">
                  <img class="w-full" src="${imageUrl}" alt="${product.title}" loading="lazy" decoding="async" width="400" height="400">
                </picture>
              </div>
            </div>
            <div class="product-card__info text-16 mt-8 min-h-[101px] desktop:min-h-[121px]">
              <p class="product-card__title font-700 desktop:text-16 text-12 pb-4 desktop:pb-8">${titleHtml}</p>
              <div class="flex flex-row-reverse justify-between items-center pb-4 desktop:pb-8">
                ${wishlistPlaceholder}
                ${vendor ? `<p class="text-12 desktop:text-14 font-300">${vendor}</p>` : ''}
              </div>
              <div class="product-card__info-top flex justify-between items-center">
                <div class="product-card__price flex float-left desktop:text-16 text-12 min-h-[25px]">
                  <span>$${formattedPrice}</span>
                </div>
                <div class="loox-rating text-12 desktop:text-16 min-h-[25px]" data-id="${product.id}"></div>
              </div>
            </div>
          </a>
        </product-card>
      </div>
    `;
  }

  /**
   * Initialize drag functionality for a single carousel
   */
  initSingleCarousel(carousel) {
    let isDragging = false;
    let startX;
    let startY;
    let scrollLeft;
    let velocity = 0;
    let lastX = 0;
    let lastTime = 0;
    let momentumId = null;

    carousel.addEventListener('mousedown', (e) => {
      if (e.target.closest('button:not(.product-card__wishlist), input, select, textarea')) return;
      isDragging = false;
      startX = e.pageX;
      startY = e.pageY;
      scrollLeft = carousel.scrollLeft;
      velocity = 0;
      lastX = e.pageX;
      lastTime = Date.now();
      if (momentumId) {
        cancelAnimationFrame(momentumId);
        momentumId = null;
      }
      carousel.style.scrollBehavior = 'auto';
    });

    const mousemoveHandler = (e) => {
      if (!startX) return;
      const moveX = e.pageX - startX;
      const moveY = e.pageY - startY;
      const distanceX = Math.abs(moveX);
      const distanceY = Math.abs(moveY);
      if (!isDragging && distanceX > 5 && distanceX > distanceY) {
        isDragging = true;
        carousel.style.cursor = 'grabbing';
        carousel.classList.add('dragging');
      }
      if (!isDragging) return;
      e.preventDefault();
      const now = Date.now();
      const deltaTime = now - lastTime;
      if (deltaTime > 0) {
        velocity = (e.pageX - lastX) / deltaTime;
      }
      lastX = e.pageX;
      lastTime = now;
      const walk = (startX - e.pageX) * 2;
      carousel.scrollLeft = scrollLeft + walk;
    };

    const mouseupHandler = () => {
      if (!startX) return;
      if (isDragging) {
        isDragging = false;
        carousel.style.cursor = 'grab';
        carousel.classList.remove('dragging');
        if (Math.abs(velocity) > 0.5) {
          const applyMomentum = () => {
            if (Math.abs(velocity) < 0.05) {
              momentumId = null;
              carousel.style.scrollBehavior = 'smooth';
              return;
            }
            carousel.scrollLeft -= velocity * 16;
            velocity *= 0.90;
            momentumId = requestAnimationFrame(applyMomentum);
          };
          applyMomentum();
        } else {
          carousel.style.scrollBehavior = 'smooth';
        }
      }
      startX = null;
      startY = null;
    };

    document.addEventListener('mousemove', mousemoveHandler);
    document.addEventListener('mouseup', mouseupHandler);
    carousel.addEventListener('dragstart', (e) => e.preventDefault());
  }

  loadTabImages(panel) {
    const images = panel.querySelectorAll('img[loading="lazy"]');
    images.forEach(img => {
      img.loading = 'eager';
      img.fetchpriority = 'high';
    });
    panel.setAttribute('data-images-loaded', 'true');
  }

  /**
   * PERF: Lazy load remaining products when user scrolls carousel
   * Uses IntersectionObserver to detect when lazy container comes into view
   */
  initLazyProductLoading() {
    if (!('IntersectionObserver' in window)) return;

    // Find panels with remaining products to load
    this.tabPanels.forEach(panel => {
      const remainingProducts = panel.getAttribute('data-remaining-products');
      if (!remainingProducts) return;

      const lazyContainer = panel.querySelector('[data-lazy-products-container]');
      if (!lazyContainer) return;

      // Create observer for this panel's lazy container
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.loadRemainingProducts(panel, lazyContainer);
            observer.disconnect(); // Only load once
          }
        });
      }, {
        root: panel.querySelector('.css-collection-carousel'),
        rootMargin: '100px', // Start loading 100px before reaching the end
        threshold: 0
      });

      observer.observe(lazyContainer);

      // Also load on scroll (fallback for horizontal scroll)
      const carousel = panel.querySelector('.css-collection-carousel');
      if (carousel) {
        let scrollTimeout;
        carousel.addEventListener('scroll', () => {
          clearTimeout(scrollTimeout);
          scrollTimeout = setTimeout(() => {
            // Check if we're near the end of the carousel
            const scrollPercent = (carousel.scrollLeft + carousel.clientWidth) / carousel.scrollWidth;
            if (scrollPercent > 0.7 && panel.getAttribute('data-remaining-products')) {
              this.loadRemainingProducts(panel, lazyContainer);
            }
          }, 100);
        }, { passive: true });
      }
    });
  }

  /**
   * Load remaining products via AJAX and append to carousel
   */
  async loadRemainingProducts(panel, container) {
    const remainingHandles = panel.getAttribute('data-remaining-products');
    if (!remainingHandles) return;

    // Mark as loading to prevent duplicate requests
    panel.removeAttribute('data-remaining-products');

    const handles = remainingHandles.split(',').filter(h => h.trim());
    if (handles.length === 0) return;

    const collectionHandle = panel.getAttribute('data-collection-handle');

    try {
      // Fetch each product and build cards
      const productPromises = handles.map(handle =>
        fetch(`/products/${handle.trim()}.json`)
          .then(res => res.ok ? res.json() : null)
          .catch(() => null)
      );

      const results = await Promise.all(productPromises);
      const products = results.filter(r => r && r.product).map(r => r.product);

      if (products.length > 0) {
        // Build and append product cards
        const html = products.map(product => this.buildProductCard(product, collectionHandle)).join('');

        // Insert products before the lazy container, then remove the container
        container.insertAdjacentHTML('beforebegin', html);
        container.remove();

        // Refresh Wishlist King (Appmate) to initialize wishlist buttons on new products
        this.refreshWishlistButtons();

        // Refresh Loox ratings for lazy-loaded products
        this.refreshLooxRatings();

        if (typeof CE2 !== 'undefined' && CE2.refresh) CE2.refresh();
      }
    } catch (error) {
      console.error('[CollectionTabs] Error loading remaining products:', error);
    }
  }

  /**
   * Refresh Wishlist King (Appmate) buttons after dynamically adding products
   * This ensures wishlist buttons are properly initialized on lazy-loaded content
   */
  refreshWishlistButtons() {
    // Check if Appmate Wishlist King is loaded
    if (typeof Appmate === 'undefined' || !Appmate.wk) {
      console.warn('[CollectionTabs] Appmate Wishlist King not loaded');
      return;
    }

    // Use requestAnimationFrame to ensure DOM is updated before refresh
    requestAnimationFrame(() => {
      try {
        // Find all lazy-loaded wishlist button placeholders
        const placeholders = this.section.querySelectorAll('.wk-lazy-button[data-wk-product-id]');

        if (placeholders.length === 0) {
          return;
        }

        // Process each placeholder
        placeholders.forEach(placeholder => {
          const productId = placeholder.getAttribute('data-wk-product-id');
          if (!productId) return;

          // Check if product is in wishlist to determine button state
          const isInWishlist = Appmate.wk.hasProduct ? Appmate.wk.hasProduct(productId) : false;

          // Determine button class and action based on wishlist state
          const btnAction = isInWishlist ? 'wk-remove-product' : 'wk-add-product';
          const btnTitle = isInWishlist ? 'Remove from Wishlist' : 'Add to Wishlist';
          const btnText = isInWishlist ? 'In Wishlist' : 'Add to Wishlist';
          const dataAttr = isInWishlist ? 'data-wk-remove-product' : 'data-wk-add-product';

          // SVG icons - filled for in wishlist, empty for not
          const filledIcon = `<svg version='1.1' xmlns='http://www.w3.org/2000/svg' width='64' height='64' viewBox='0 0 64 64'>
            <path d='M47.199 4c-6.727 0-12.516 5.472-15.198 11.188-2.684-5.715-8.474-11.188-15.201-11.188-9.274 0-16.8 7.527-16.8 16.802 0 18.865 19.031 23.812 32.001 42.464 12.26-18.536 31.999-24.2 31.999-42.464 0-9.274-7.527-16.802-16.801-16.802z' fill='#F84141'></path>
          </svg>`;
          const emptyIcon = `<svg version='1.1' xmlns='http://www.w3.org/2000/svg' width='64px' height='60.833px' viewBox='0 0 64 60.833'>
            <path stroke='#000' stroke-width='5' stroke-miterlimit='10' fill-opacity='0' d='M45.684,2.654c-6.057,0-11.27,4.927-13.684,10.073 c-2.417-5.145-7.63-10.073-13.687-10.073c-8.349,0-15.125,6.776-15.125,15.127c0,16.983,17.134,21.438,28.812,38.231 c11.038-16.688,28.811-21.787,28.811-38.231C60.811,9.431,54.033,2.654,45.684,2.654z'/>
          </svg>`;
          const icon = isInWishlist ? filledIcon : emptyIcon;

          // Create the button HTML
          const buttonHtml = `
            <button type="button" class="wk-button-collection ${btnAction}" title="${btnTitle}" ${dataAttr}="${productId}">
              <div class="wk-icon">${icon}</div>
              <span class="wk-label">${btnText}</span>
            </button>`;

          // Replace placeholder with actual button
          placeholder.innerHTML = buttonHtml;
          placeholder.classList.remove('wk-lazy-button');
          placeholder.classList.add('wk-button-rendered');
        });

        // Call Appmate render to ensure event handlers are bound
        if (typeof Appmate.wk.render === 'function') {
          Appmate.wk.render();
        }

      } catch (e) {
        console.warn('[CollectionTabs] Could not refresh wishlist buttons:', e);
      }
    });
  }

  /**
   * Refresh Loox review ratings after dynamically adding products
   * This ensures review widgets are properly initialized on lazy-loaded content
   * Loox is deferred (loads after 5s or scroll > 300px), so we may need to retry
   */
  refreshLooxRatings() {
    const self = this;
    let attempts = 0;
    const maxAttempts = 10;

    const tryInitLoox = () => {
      attempts++;

      // Check if Loox is loaded and has the init function
      if (typeof Loox !== 'undefined' && typeof Loox.init === 'function') {
        requestAnimationFrame(() => {
          try {
            Loox.init();
          } catch (e) {
            console.warn('[CollectionTabs] Could not refresh Loox ratings:', e);
          }
        });
      } else if (attempts < maxAttempts) {
        // Loox not loaded yet, retry after a delay
        // Loox defers loading until scroll > 300px or 5 seconds
        setTimeout(tryInitLoox, 500);
      } else {
        console.warn('[CollectionTabs] Loox not available after', maxAttempts, 'attempts');
      }
    };

    // Start trying to init Loox
    tryInitLoox();
  }

  /**
   * Update a single wishlist button's visual state after add/remove
   * Called by Appmate event handlers
   */
  updateWishlistButtonState(productId, isInWishlist) {
    if (!this.section) return;

    // Find the button for this product
    const button = this.section.querySelector(`[data-wk-add-product="${productId}"], [data-wk-remove-product="${productId}"]`);
    if (!button) return;

    const btnAction = isInWishlist ? 'wk-remove-product' : 'wk-add-product';
    const btnTitle = isInWishlist ? 'Remove from Wishlist' : 'Add to Wishlist';
    const dataAttr = isInWishlist ? 'data-wk-remove-product' : 'data-wk-add-product';
    const oldDataAttr = isInWishlist ? 'data-wk-add-product' : 'data-wk-remove-product';

    // Update button classes
    button.classList.remove('wk-add-product', 'wk-remove-product');
    button.classList.add(btnAction);
    button.setAttribute('title', btnTitle);

    // Update data attribute
    button.removeAttribute(oldDataAttr);
    button.setAttribute(dataAttr, productId);

    // Update icon
    const iconContainer = button.querySelector('.wk-icon');
    if (iconContainer) {
      if (isInWishlist) {
        iconContainer.innerHTML = `<svg version='1.1' xmlns='http://www.w3.org/2000/svg' width='64' height='64' viewBox='0 0 64 64'>
          <path d='M47.199 4c-6.727 0-12.516 5.472-15.198 11.188-2.684-5.715-8.474-11.188-15.201-11.188-9.274 0-16.8 7.527-16.8 16.802 0 18.865 19.031 23.812 32.001 42.464 12.26-18.536 31.999-24.2 31.999-42.464 0-9.274-7.527-16.802-16.801-16.802z' fill='#F84141'></path>
        </svg>`;
      } else {
        iconContainer.innerHTML = `<svg version='1.1' xmlns='http://www.w3.org/2000/svg' width='64px' height='60.833px' viewBox='0 0 64 60.833'>
          <path stroke='#000' stroke-width='5' stroke-miterlimit='10' fill-opacity='0' d='M45.684,2.654c-6.057,0-11.27,4.927-13.684,10.073 c-2.417-5.145-7.63-10.073-13.687-10.073c-8.349,0-15.125,6.776-15.125,15.127c0,16.983,17.134,21.438,28.812,38.231 c11.038-16.688,28.811-21.787,28.811-38.231C60.811,9.431,54.033,2.654,45.684,2.654z'/>
        </svg>`;
      }
    }

    // Update label
    const label = button.querySelector('.wk-label');
    if (label) {
      label.textContent = isInWishlist ? 'In Wishlist' : 'Add to Wishlist';
    }
  }
}

/**
 * Initialize all Collection Tabs Carousel sections on the page
 * Each section gets its own instance for proper scoping
 */
function initAllCollectionTabsCarousels() {
  const sections = document.querySelectorAll('.collection-tabs-carousel');
  sections.forEach(section => {
    new CollectionTabsCarousel(section);
  });
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  // Use requestIdleCallback for non-critical initialization
  if ('requestIdleCallback' in window) {
    requestIdleCallback(() => {
      initAllCollectionTabsCarousels();
    });
  } else {
    // Fallback for browsers without requestIdleCallback
    setTimeout(() => {
      initAllCollectionTabsCarousels();
    }, 0);
  }
});
